import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ShoppingCart, Trash2, Download, Plus, Minus, Check, X, ChefHat } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ShoppingList = () => {
  const { t, getLocalizedText, language } = useLanguage();
  const [recipes, setRecipes] = useState([]);
  const [shoppingList, setShoppingList] = useState(() => {
    const saved = localStorage.getItem('love4vegan_shopping_list');
    return saved ? JSON.parse(saved) : [];
  });
  const [showRecipes, setShowRecipes] = useState(false);
  const [newItem, setNewItem] = useState('');

  useEffect(() => {
    fetchRecipes();
  }, []);

  useEffect(() => {
    localStorage.setItem('love4vegan_shopping_list', JSON.stringify(shoppingList));
  }, [shoppingList]);

  const fetchRecipes = async () => {
    try {
      const response = await axios.get(`${API}/recipes`);
      setRecipes(response.data);
    } catch (error) {
      console.error('Error fetching recipes:', error);
    }
  };

  const texts = {
    title: { fi: 'Kauppalista', fr: 'Liste des courses', en: 'Shopping List' },
    addFromRecipes: { fi: 'Lisää resepteistä', fr: 'Ajouter des recettes', en: 'Add from recipes' },
    addItem: { fi: 'Lisää tuote', fr: 'Ajouter un article', en: 'Add item' },
    placeholder: { fi: 'Kirjoita tuote...', fr: 'Écrire un article...', en: 'Type an item...' },
    emptyList: { fi: 'Kauppalistasi on tyhjä', fr: 'Votre liste est vide', en: 'Your shopping list is empty' },
    addIngredientsFrom: { fi: 'Lisää ainekset reseptistä:', fr: 'Ajouter les ingrédients de:', en: 'Add ingredients from:' },
    download: { fi: 'Lataa lista', fr: 'Télécharger', en: 'Download list' },
    clearAll: { fi: 'Tyhjennä lista', fr: 'Vider la liste', en: 'Clear all' },
    ingredientsAdded: { fi: 'Ainekset lisätty!', fr: 'Ingrédients ajoutés!', en: 'Ingredients added!' },
    close: { fi: 'Sulje', fr: 'Fermer', en: 'Close' },
    selectRecipes: { fi: 'Valitse reseptit', fr: 'Sélectionner recettes', en: 'Select recipes' },
    noRecipes: { fi: 'Ei reseptejä vielä', fr: 'Pas encore de recettes', en: 'No recipes yet' }
  };

  const addItemToList = () => {
    if (!newItem.trim()) return;
    setShoppingList(prev => [...prev, { id: Date.now(), text: newItem.trim(), checked: false }]);
    setNewItem('');
  };

  const addIngredientsFromRecipe = (recipe) => {
    const ingredientsText = getLocalizedText(recipe.ingredients);
    if (!ingredientsText) return;

    // Parse ingredients (split by newlines, remove bullets/dashes)
    const lines = ingredientsText.split('\n')
      .map(line => line.replace(/^[-•*]\s*/, '').trim())
      .filter(line => line.length > 0);

    const newItems = lines.map(line => ({
      id: Date.now() + Math.random(),
      text: line,
      checked: false,
      recipeId: recipe.id,
      recipeName: getLocalizedText(recipe.title)
    }));

    setShoppingList(prev => [...prev, ...newItems]);
    toast.success(getLocalizedText(texts.ingredientsAdded));
  };

  const toggleItem = (id) => {
    setShoppingList(prev => prev.map(item => 
      item.id === id ? { ...item, checked: !item.checked } : item
    ));
  };

  const removeItem = (id) => {
    setShoppingList(prev => prev.filter(item => item.id !== id));
  };

  const clearList = () => {
    setShoppingList([]);
  };

  const downloadList = () => {
    const uncheckedItems = shoppingList.filter(item => !item.checked);
    const checkedItems = shoppingList.filter(item => item.checked);
    
    let content = `🌱 love4vegan - ${getLocalizedText(texts.title)}\n`;
    content += `${new Date().toLocaleDateString(language === 'fi' ? 'fi-FI' : language === 'fr' ? 'fr-FR' : 'en-GB')}\n\n`;
    
    if (uncheckedItems.length > 0) {
      content += uncheckedItems.map(item => `☐ ${item.text}`).join('\n');
    }
    
    if (checkedItems.length > 0) {
      content += '\n\n--- ✓ ---\n';
      content += checkedItems.map(item => `☑ ${item.text}`).join('\n');
    }

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `love4vegan-${getLocalizedText(texts.title).toLowerCase().replace(/\s/g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const uncheckedCount = shoppingList.filter(item => !item.checked).length;
  const checkedCount = shoppingList.filter(item => item.checked).length;

  return (
    <div className="min-h-screen py-12" data-testid="shopping-list-page">
      <div className="max-w-2xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <ShoppingCart className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight">
            {getLocalizedText(texts.title)}
          </h1>
        </div>

        {/* Add Item Form */}
        <div className="flex gap-2 mb-6">
          <Input
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addItemToList()}
            placeholder={getLocalizedText(texts.placeholder)}
            className="flex-1 rounded-full"
            data-testid="add-item-input"
          />
          <Button 
            onClick={addItemToList}
            className="rounded-full bg-primary"
            data-testid="add-item-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            {getLocalizedText(texts.addItem)}
          </Button>
        </div>

        {/* Add from Recipes Button */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => setShowRecipes(!showRecipes)}
            className="w-full rounded-xl border-dashed border-2"
            data-testid="add-from-recipes-btn"
          >
            <ChefHat className="h-4 w-4 mr-2" />
            {getLocalizedText(texts.addFromRecipes)}
          </Button>

          {/* Recipe Selector */}
          {showRecipes && (
            <div className="mt-4 p-4 bg-card rounded-2xl border border-border animate-fadeIn">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">{getLocalizedText(texts.selectRecipes)}</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowRecipes(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              {recipes.length > 0 ? (
                <div className="grid gap-2 max-h-64 overflow-y-auto">
                  {recipes.map(recipe => (
                    <button
                      key={recipe.id}
                      onClick={() => addIngredientsFromRecipe(recipe)}
                      className="flex items-center gap-3 p-3 rounded-xl bg-muted/20 hover:bg-muted/40 transition-colors text-left"
                      data-testid={`recipe-select-${recipe.id}`}
                    >
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted/30 flex-shrink-0">
                        {(recipe.image_data || recipe.image_url) ? (
                          <img 
                            src={recipe.image_data || recipe.image_url} 
                            alt="" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ChefHat className="h-5 w-5 text-muted" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{getLocalizedText(recipe.title)}</p>
                        <p className="text-xs text-foreground/50">{t(`recipes.${recipe.category}`)}</p>
                      </div>
                      <Plus className="h-5 w-5 text-primary flex-shrink-0" />
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-center py-4 text-foreground/50">{getLocalizedText(texts.noRecipes)}</p>
              )}
            </div>
          )}
        </div>

        {/* Shopping List */}
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          {shoppingList.length > 0 ? (
            <>
              {/* Unchecked Items */}
              <div className="divide-y divide-border/50">
                {shoppingList.filter(item => !item.checked).map(item => (
                  <div 
                    key={item.id} 
                    className="flex items-center gap-3 p-4 group"
                    data-testid={`list-item-${item.id}`}
                  >
                    <button
                      onClick={() => toggleItem(item.id)}
                      className="w-6 h-6 rounded-full border-2 border-primary flex items-center justify-center hover:bg-primary/10 transition-colors"
                      data-testid={`check-item-${item.id}`}
                    >
                    </button>
                    <span className="flex-1">{item.text}</span>
                    {item.recipeName && (
                      <span className="text-xs text-foreground/40 hidden sm:block">{item.recipeName}</span>
                    )}
                    <button
                      onClick={() => removeItem(item.id)}
                      className="p-1 text-foreground/30 hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-item-${item.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Checked Items */}
              {checkedCount > 0 && (
                <div className="bg-muted/10 divide-y divide-border/30">
                  {shoppingList.filter(item => item.checked).map(item => (
                    <div 
                      key={item.id} 
                      className="flex items-center gap-3 p-4 group"
                      data-testid={`list-item-${item.id}`}
                    >
                      <button
                        onClick={() => toggleItem(item.id)}
                        className="w-6 h-6 rounded-full bg-primary flex items-center justify-center"
                        data-testid={`uncheck-item-${item.id}`}
                      >
                        <Check className="h-4 w-4 text-primary-foreground" />
                      </button>
                      <span className="flex-1 line-through text-foreground/40">{item.text}</span>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="p-1 text-foreground/30 hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="py-16 text-center">
              <ShoppingCart className="h-12 w-12 text-muted mx-auto mb-4" />
              <p className="text-foreground/50">{getLocalizedText(texts.emptyList)}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        {shoppingList.length > 0 && (
          <div className="flex gap-3 mt-6">
            <Button
              onClick={downloadList}
              variant="outline"
              className="flex-1 rounded-full"
              data-testid="download-list-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              {getLocalizedText(texts.download)}
            </Button>
            <Button
              onClick={clearList}
              variant="ghost"
              className="text-destructive hover:text-destructive hover:bg-destructive/10 rounded-full"
              data-testid="clear-list-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {getLocalizedText(texts.clearAll)}
            </Button>
          </div>
        )}

        {/* Stats */}
        {shoppingList.length > 0 && (
          <p className="text-center text-sm text-foreground/40 mt-4">
            {uncheckedCount} {getLocalizedText({ fi: 'jäljellä', fr: 'restants', en: 'remaining' })}
            {checkedCount > 0 && ` • ${checkedCount} ✓`}
          </p>
        )}
      </div>
    </div>
  );
};
